/**
 * @fileoverview Gather Information — section overview
 *
 * Landing page for the "Gather Information" sidebar group. Mirrors the Data
 * (SummarizationView) and Investigations (ReviewOverview) overviews — a titled
 * `bg-muted` page with a single grouped card whose `divide-y` rows match the
 * AgentCard layout — so the three accordion groups read consistently.
 *
 * The sub-areas — Searches, Day One, Files — each link to their own phase
 * route. (Mail lives as its own top-level job nav item under Job Chat.)
 */

import { useNavigate } from "@tanstack/react-router";
import { Search, Stamp, Files, Sparkles, type LucideIcon } from "lucide-react";
import { Button } from "@/ui/primitives/button";

interface GatherInformationOverviewProps {
  jobId: string;
}

type SectionLink = {
  label: string;
  description: string;
  icon: LucideIcon;
  to:
    | "/dashboard/jobs/$jobId/searches"
    | "/dashboard/jobs/$jobId/day-one"
    | "/dashboard/jobs/$jobId/gather";
};

const SECTIONS: ReadonlyArray<SectionLink> = [
  {
    label: "Searches",
    description:
      "Run registry, web, and KYC/AML searches to build out the picture of the company and its directors.",
    icon: Search,
    to: "/dashboard/jobs/$jobId/searches",
  },
  {
    label: "Document Generator",
    description:
      "Generate and track the day-one precedent documents for the appointment.",
    icon: Stamp,
    to: "/dashboard/jobs/$jobId/day-one",
  },
  {
    label: "Files",
    description:
      "Upload, organise, and review every document in the job's library.",
    icon: Files,
    to: "/dashboard/jobs/$jobId/gather",
  },
];

export function GatherInformationOverview({
  jobId,
}: GatherInformationOverviewProps) {
  const navigate = useNavigate();

  return (
    // Mirrors SummarizationView / ReviewOverview bleed-to-edges layout.
    <div className="-m-6 flex flex-1 flex-col gap-5 bg-muted px-6 pb-6 pt-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Gather Information</h3>
          <div className="mt-1 flex items-center gap-3">
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Sparkles className="h-3 w-3" />
              {SECTIONS.length} sections
            </span>
          </div>
        </div>
      </div>

      {/* Single grouped card — mirrors the Data / Investigations group cards. */}
      <div className="overflow-hidden rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] transition-shadow duration-200 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)]">
        <div className="flex items-baseline justify-between px-5 py-3">
          <h4 className="text-sm font-semibold">Sections</h4>
        </div>
        <div className="divide-y divide-border">
          {SECTIONS.map((section) => (
            <SectionRow
              key={section.label}
              section={section}
              onOpen={() => navigate({ to: section.to, params: { jobId } })}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * One row inside the grouped card. Replicates the AgentCard "grouped" row
 * layout (icon circle → title + blurb → Open button) so the look matches the
 * Data and Investigations overviews exactly.
 */
function SectionRow({
  section,
  onOpen,
}: {
  section: SectionLink;
  onOpen: () => void;
}) {
  const Icon = section.icon;
  return (
    <div className="group bg-background transition-colors duration-150 hover:bg-muted/40">
      <div
        role="button"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen();
          }
        }}
        className="w-full cursor-pointer text-left"
      >
        <div className="flex items-center gap-4 px-5 py-3.5">
          {/* Icon circle — mirrors AgentIcon's completed (green) treatment. */}
          <div className="shrink-0">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/40">
              <Icon className="h-4 w-4 text-emerald-600 dark:text-emerald-500" />
            </div>
          </div>

          {/* Main content */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2.5">
              <span className="text-sm font-semibold text-foreground">
                {section.label}
              </span>
            </div>
            <p className="mt-2 line-clamp-2 text-xs text-muted-foreground">
              {section.description}
            </p>
          </div>

          {/* Action area */}
          <div
            className="flex shrink-0 items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <Button size="sm" onClick={onOpen} className="h-9 gap-1.5">
              Open
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
